export const KYC_DESIGNATION_OPTIONS = [
	'STEM Teacher',
	'ICT Teacher',
	'Technology/ Media Specialist',
	'Classroom Teacher',
	'Science Teacher',
	'Curriculum Coordinator',
	'Curriculum Director',
	'Asst. Principal',
	'Principal',
	'District Administrator',
	'Homeschool Teacher/ Private Tutor',
	'Parent',
	'Others',
] as const;
export type KycDesignation = (typeof KYC_DESIGNATION_OPTIONS)[number];

export const KYC_INSTITUTE_TYPE_OPTIONS = [
	'Public School',
	'Private School',
	'Home School',
	'Charter School',
	'Private Tuition',
	'Others',
] as const;
export type KycInstituteType = (typeof KYC_INSTITUTE_TYPE_OPTIONS)[number];

export const KYC_LEAD_SOURCE_OPTIONS = [
	'Google Search',
	'Colleague or Friend',
	'Facebook Group',
	'Conference or Workshop',
	'AI Assistant (e.g., ChatGPT or Gemini)',
	'Email',
	'Newsletter, Podcast, Blog, or Article',
	'Clever / ClassLink',
	'Social Media (Instagram, TikTok, YouTube, etc.)',
	'Other',
] as const;
export type KycLeadSource = (typeof KYC_LEAD_SOURCE_OPTIONS)[number];

export const KYC_NO_SCHOOL_DESIGNATIONS: readonly KycDesignation[] = [
	'Homeschool Teacher/ Private Tutor',
	'Parent',
	'Others',
];
export const canDesignationHaveNoSchool = (designation: string): boolean =>
	(KYC_NO_SCHOOL_DESIGNATIONS as readonly string[]).includes(designation);

export type SchoolType = KycInstituteType;

export type School = {
	id: string;
	source: 'NCES_CCD' | 'NCES_PSS' | 'GOOGLE_PLACES' | 'MANUAL_ENTRY';
	name: string;
	city: string;
	state: string;
	countryCode: string;
	postalCode?: string | null;
	schoolType?: SchoolType | null;
	level?: string | null;
	districtName?: string | null;
};

export type PlacesSchoolResult = {
	placesId: string;
	name: string;
	formattedAddress?: string | null;
	city?: string | null;
	region?: string | null;
	countryCode?: string | null;
};

export type PlacesSearchResult =
	{ status: 'AVAILABLE'; results: PlacesSchoolResult[] } | { status: 'UNAVAILABLE' | 'NOT_REQUESTED' };

export const SCHOOL_SEARCH_LIMIT = 10;

export type SchoolSearchResponse = {
	schools: School[];
	places: PlacesSearchResult;
};

export type KycPersonalInfo = {
	designation: string;
};

export type KycUseCase = {
	teacherGrades: string[];
	usageIntention: string;
	leadSource: string;
};

export type KycSchoolInfoV1 = {
	instituteName: string;
	instituteType: string;
	cityAndState: string;
};

export type KycDetailsV1 = {
	version: '1';
	personalInfo: KycPersonalInfo;
	schoolInfo: KycSchoolInfoV1;
	useCase: KycUseCase;
};

export type KycVerifiedSchool = {
	source: 'verified_school';
	schoolId: string;
};
export type KycGooglePlacesSchool = {
	source: 'google_places';
	placesId: string;
	schoolType: SchoolType;
};
export type KycManualEntrySchool = {
	source: 'manual_entry';
	schoolName: string;
	country: string;
	region: string;
	city: string;
	schoolType: SchoolType;
};
export type KycNoSchool = {
	source: 'no_school';
};
export type KycSchoolInfoV2 = KycVerifiedSchool | KycGooglePlacesSchool | KycManualEntrySchool | KycNoSchool;

export type KycDetailsV2 = {
	version: '2';
	personalInfo: KycPersonalInfo;
	schoolInfo: KycSchoolInfoV2;
	useCase: KycUseCase;
};

export type KycDetails = KycDetailsV1 | KycDetailsV2;

export type KycChosenVerifiedSchool = KycVerifiedSchool & { school: School };
export type KycChosenGooglePlacesSchool = KycGooglePlacesSchool & { place: PlacesSchoolResult };
export type KycChosenSchool = KycChosenVerifiedSchool | KycChosenGooglePlacesSchool | KycManualEntrySchool;

export type KycResolvedSchool = {
	source: 'verified_school' | 'google_places' | 'manual_entry';
	placesId: string | null;
	school: School;
};
export type KycSchoolInfoV2Response = KycResolvedSchool | KycNoSchool;

export type KycDetailsV2Response = Omit<KycDetailsV2, 'schoolInfo'> & {
	schoolInfo: KycSchoolInfoV2Response;
};

export type KycProfile = KycDetailsV1 | KycDetailsV2Response;
